import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import { v1 as uuidv1 } from 'uuid';
import { useHistory } from 'react-router-dom';

import css from './AddToFavoriteButton.module.css';
import IconHeart from '../../../common/components/IconHeart/IconHeart';
import { FormattedText } from '../../../../util/data';
import { InlineTextButton, Modal, Form, Button, FieldTextInput } from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { updateFavoritesListOfUser } from '../../../../ducks/user.duck';
import { createSlug } from '../../../../util/urlHelpers';
import { required } from '../../../../util/validators';
import {
  MAX_OF_LISTINGS_IN_ONE_LIST,
  MAX_OF_FAVORITES_LIST_OF_A_USER,
  favoriteDefaultImages,
} from '../../config';
import { createResourceLocatorString } from '../../../../util/routes';
import routeConfiguration from '../../../../routing/routeConfiguration';

const OldFavoriteList = props => {
  const { list, currentListing, listData, setListData } = props;
  const intl = useIntl();
  const listingId = currentListing.id.uuid;
  const isAddedToList = list.listingIds.includes(listingId);

  const handleRemoveItemFromList = () => {
    const newListData = listData.map(item => {
      if (item.id === list.id) {
        return { ...item, listingIds: item.listingIds.filter(id => id !== listingId) };
      }

      return item;
    });
    setListData(newListData);
  };

  const handleAddItemToList = () => {
    const newListData = listData.map(item => {
      if (item.id === list.id) {
        return { ...item, listingIds: [...item.listingIds, listingId] };
      }

      return item;
    });
    setListData(newListData);
  };

  const isCannotAddToList = list.listingIds.length >= MAX_OF_LISTINGS_IN_ONE_LIST;

  return (
    <div key={list.id} className={css.listContainer}>
      <div className={css.infoContainer}>
        <InlineTextButton
          onClick={isAddedToList ? handleRemoveItemFromList : handleAddItemToList}
          disabled={isCannotAddToList && !isAddedToList}
        >
          <IconHeart isActive={isAddedToList} isDisable={isCannotAddToList && !isAddedToList} />
        </InlineTextButton>
        <div className={css.listInfo}>
          <FormattedText
            id="AddToFavoriteModal.numOfItems"
            values={{ count: list.listingIds.length }}
          />
          <p>{list.name}</p>
        </div>
      </div>
      {isAddedToList ? (
        <InlineTextButton onClick={handleRemoveItemFromList}>
          {intl.formatMessage({ id: 'AddToFavoriteModal.removeButton' })}
        </InlineTextButton>
      ) : (
        <InlineTextButton onClick={handleAddItemToList} disabled={isCannotAddToList}>
          {intl.formatMessage({ id: 'AddToFavoriteModal.addButton' })}
        </InlineTextButton>
      )}
    </div>
  );
};

const AddToFavoriteButton = props => {
  const { listing, customHeartComponent, customHeartComponentClassName } = props;
  const { currentUser } = useSelector(state => state.user);

  const { title } = listing.attributes || {};
  const { favoriteLists = [] } = currentUser?.attributes.profile.metadata || {};

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const [listData, setListData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onManageDisableScrolling = (componentId, disableScrolling) => {
    dispatch(manageDisableScrolling(componentId, disableScrolling));
  };

  useEffect(() => {
    setListData(favoriteLists);
  }, [currentUser]);

  const handleOpenModal = e => {
    e.preventDefault();
    e.stopPropagation();

    if (currentUser) {
      setIsModalOpen(true);
    } else {
      history.push(createResourceLocatorString('LoginPage', routeConfiguration(), {}, {}));
    }
  };

  const handleCloseModal = () => {
    if (!isEqual(favoriteLists, listData)) {
      dispatch(updateFavoritesListOfUser({ favoriteLists: listData }));
    }
    setIsModalOpen(false);
  };

  const handleCreateNewList = values => {
    return new Promise(resolve => {
      const { newListName } = values;
      let newId = uuidv1();

      while (listData.find(item => item.id === newId)) {
        newId = uuidv1();
      }

      setListData(
        listData.concat({
          id: newId,
          key: createSlug(newListName),
          name: newListName,
          listingIds: [listing.id.uuid],
          img: Math.floor(Math.random() * favoriteDefaultImages.length),
        })
      );

      resolve();
    });
  };

  const isBelongToAtLeastOneList = listData.find(list => {
    return list.listingIds.find(id => id === listing.id.uuid);
  });

  const hasReachedLimitOfFavoritesList = listData.length >= MAX_OF_FAVORITES_LIST_OF_A_USER;
  return (
    <>
      <div className={customHeartComponentClassName || css.iconWrapper} onClick={handleOpenModal}>
        <IconHeart className={css.heartIcon} isActive={isBelongToAtLeastOneList} />
        {customHeartComponent && (
          <>
            &nbsp;&nbsp;
            {customHeartComponent}
          </>
        )}
      </div>
      <Modal
        id="AddToFavoriteModal.favoriteLists"
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        contentClassName={css.addToFavoriteModal}
      >
        <FormattedText
          id="AddToFavoriteModal.title"
          values={{ listingName: title }}
          className={css.modalTitle}
        />
        {listData.length > 0 && (
          <>
            <FormattedText id="AddToFavoriteModal.yourLists" className={css.label} />
            <div className={css.oldListContainer}>
              {listData.map(list => (
                <OldFavoriteList
                  key={list.id}
                  list={list}
                  currentListing={listing}
                  listData={listData}
                  setListData={setListData}
                />
              ))}
            </div>
          </>
        )}

        <FinalForm
          onSubmit={handleCreateNewList}
          render={fieldRenderProps => {
            const { handleSubmit, invalid, form } = fieldRenderProps;
            return (
              <Form
                onSubmit={e => {
                  handleSubmit(e).then(() => {
                    form.reset();
                    form.resetFieldState('newListName');
                  });
                }}
              >
                <FieldTextInput
                  type="text"
                  id="newListName"
                  name="newListName"
                  label={intl.formatMessage({ id: 'AddToFavoriteModal.newListNameLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'AddToFavoriteModal.newListNamePlaceholder',
                  })}
                  validate={required(
                    intl.formatMessage({
                      id: 'AddToFavoriteModal.newListNameRequired',
                    })
                  )}
                  className={css.field}
                />
                {hasReachedLimitOfFavoritesList && (
                  <FormattedText id="AddToFavoriteModal.limitReached" className={css.noteText} />
                )}

                <Button
                  className={css.button}
                  type="submit"
                  disabled={invalid || hasReachedLimitOfFavoritesList}
                >
                  {intl.formatMessage({ id: 'AddToFavoriteModal.createNewList' })}
                </Button>
              </Form>
            );
          }}
        />
      </Modal>
    </>
  );
};

export default AddToFavoriteButton;
